import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { useConversation } from '../../context/ConversationProvider';
import { useMessages } from '../../context/MessageProvider';
import { usePresence } from '../../context/UserPresenceProvider';
import MessageBubble from './MessageBubble';
import MessageInputBar from './MessageInputBar';
import TypingIndicator from './TypingIndicator';
import useChatUser from '../../hooks/useChatUser';
import { db } from '../../../../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { Virtuoso } from 'react-virtuoso';
import { getStableMessageKey } from '../../utils/messageUtils';
import { IconChevronDown, IconChevronUp, IconSearch, IconX } from '@tabler/icons-react';
import './ChatWindow.css';


const ChatWindow = ({ hideHeader = false }) => {
  // Use new modular contexts
  const {
    activeConversationId,
    conversations,
    membersByConversation,
    leaveGroupConversation,
    currentUser,
  } = useConversation();
  const {
    messages: contextMessages,
    loadingMore,
    hasMore,
    loadMoreMessages,
    scrollToBottom,
    virtuosoRef, // Use ref from context instead of creating local ref
  } = useMessages();
  const { presenceByUserId, typingByConversation } = usePresence();

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const [memberDetails, setMemberDetails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAtTop, setIsAtTop] = useState(false);

  // Use messages from context (already filtered by active conversation)
  const messages = contextMessages;

  // FIX D4: Memoize activeConv .find() to prevent re-running on every render
  const activeConv = useMemo(
    () => (conversations || []).find(c => c.id === activeConversationId) || null,
    [conversations, activeConversationId]
  );
  const isGroup = activeConv && activeConv.type === 'group';
  const groupMembers = useMemo(
    () => isGroup ? (membersByConversation?.[activeConversationId] || []) : [],
    [isGroup, membersByConversation, activeConversationId]
  );
  const memberCount = isGroup
    ? (groupMembers.length || (Array.isArray(activeConv?.memberIds) ? activeConv.memberIds.length : 0))
    : 0;
  const typingMap = typingByConversation[activeConversationId] || {};
  const typingUserIds = Object.keys(typingMap).filter(uid => uid !== currentUser?.uid);
  const typingNames = typingUserIds.map((uid) => {
    const presence = presenceByUserId?.[uid];
    return presence?.displayName || presence?.username || 'Someone';
  });

  // Direct chat user logic
  const directMemberIds = !isGroup && activeConv ? (Array.isArray(activeConv.memberIds) ? activeConv.memberIds : []) : [];
  const otherUserId = !isGroup && currentUser?.uid ? directMemberIds.find(id => id !== currentUser.uid) : null;
  const { user: directUser, isOnline: isDirectUserOnline } = useChatUser(otherUserId);

  // 1. Local search within currently loaded messages
  const localFilteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const queryLower = searchQuery.toLowerCase();
    return messages.filter(msg =>
      msg.content?.toLowerCase().includes(queryLower) ||
      msg.senderName?.toLowerCase().includes(queryLower)
    );
  }, [messages, searchQuery]);

  // 2. Server-side search for older messages
  // FIX 9.1: Cancellation token prevents stale results from overwriting newer ones
  useEffect(() => {
    if (!searchQuery.trim() || !activeConversationId) {
      setSearchResults([]);
      return;
    }

    let isCancelled = false;

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const q = query(
          collection(db, 'messages'),
          where('conversationId', '==', activeConversationId),
          where('content', '>=', searchQuery),
          where('content', '<=', searchQuery + '\uf8ff'),
          orderBy('content'),
          limit(20)
        );

        const snap = await getDocs(q);
        if (isCancelled) return;
        const results = snap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null
          };
        });
        setSearchResults(results);
      } catch (error) {
        if (isCancelled) return;
        console.error('[ChatWindow] Search failed', error);
      } finally {
        if (!isCancelled) setIsSearching(false);
      }
    }, 500);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, activeConversationId]);

  // Hybrid search: Combine local matches with server results
  const displayMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages;

    const combined = [...localFilteredMessages, ...searchResults];
    const seenIds = new Set();
    const unique = combined.filter(msg => {
      if (seenIds.has(msg.id)) return false;
      seenIds.add(msg.id);
      return true;
    });

    // FIX: Pre-compute timestamps to avoid creating new Date objects during sort (O(n) vs O(n log n))
    const withTimestamps = unique.map(msg => ({
      ...msg,
      _sortTime: msg.createdAt?.getTime ? msg.createdAt.getTime() : (msg.createdAt || 0)
    }));

    // FIX H-4: Sort ascending (oldest first) to match Virtuoso alignToBottom
    return withTimestamps.sort((a, b) => a._sortTime - b._sortTime);
  }, [searchQuery, messages, localFilteredMessages, searchResults]);

  // Compute stable member IDs string
  const memberIdsKey = useMemo(() => {
    return groupMembers.map(m => m.userId).sort().join(',');
  }, [groupMembers]);

  // Fetch member details
  // FIX 9.2: Cancellation token prevents stale member data from overwriting newer data
  useEffect(() => {
    if (!isGroup || !groupMembers.length) {
      setMemberDetails([]);
      return;
    }

    let isCancelled = false;

    const fetchMembers = async () => {
      try {
        const uniqueIds = [...new Set(groupMembers.map(m => m.userId))];
        const details = [];
        for (let i = 0; i < uniqueIds.length; i += 10) {
          if (isCancelled) return;
          const chunk = uniqueIds.slice(i, i + 10);
          const q = query(collection(db, 'users'), where('uid', 'in', chunk));
          const snap = await getDocs(q);
          snap.forEach(doc => details.push({ id: doc.id, ...doc.data() }));
        }
        if (!isCancelled) {
          setMemberDetails(details);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[ChatWindow] Failed to fetch member details', error);
        }
      }
    };

    fetchMembers();

    return () => {
      isCancelled = true;
    };
  }, [isGroup, memberIdsKey, groupMembers]);

  // Close member list when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMemberList && !e.target.closest('.chat-window__member-list') && !e.target.closest('.chat-window__members-btn')) {
        setShowMemberList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMemberList]);

  // FIX A1: Scroll to the last message once when a conversation is opened or
  // messages first arrive. Virtuoso's followOutput only follows NEW messages
  // when already at the bottom — it does nothing when switching conversations
  // (the scroller stays where the previous conversation left it → top).
  // This effect fires once per conversationId switch, after messages load.
  const scrolledForConvRef = useRef(null);
  useEffect(() => {
    if (!activeConversationId || messages.length === 0) return;
    // Already scrolled for this conversation — don't re-scroll on new messages
    if (scrolledForConvRef.current === activeConversationId) return;
    scrolledForConvRef.current = activeConversationId;
    // Use 'auto' (instant) — smooth can undershoot with dynamic heights
    virtuosoRef.current?.scrollToIndex({
      index: messages.length - 1,
      align: 'end',
      behavior: 'auto',
    });
  }, [activeConversationId, messages.length, virtuosoRef]);

  // Stable Virtuoso component references — must be declared before any early returns
  // so React hooks are always called in the same order on every render.
  const VirtuosoHeader = useCallback(() => (
    hasMore && isAtTop && displayMessages.length > 0 ? (
      <div className="chat-window__load-more">
        <button
          onClick={loadMoreMessages}
          disabled={loadingMore}
          className="chat-window__load-more-btn"
        >
          {loadingMore ? 'Loading...' : 'Load older messages'}
        </button>
      </div>
    ) : null
  ), [hasMore, isAtTop, displayMessages.length, loadMoreMessages, loadingMore]);

  const VirtuosoFooter = useCallback(() => (
    <TypingIndicator names={typingNames} />
  ), [typingNames]);

  const virtuosoComponents = useMemo(() => ({
    Header: VirtuosoHeader,
    Footer: VirtuosoFooter,
  }), [VirtuosoHeader, VirtuosoFooter]);

  // FIX D2: Wrap handleLeaveGroup in useCallback to prevent unnecessary re-renders
  const handleLeaveGroup = useCallback(async () => {
    if (!isGroup || !activeConv) return;
    await leaveGroupConversation(activeConv.id);
  }, [isGroup, activeConv, leaveGroupConversation]);

  // FIX: Extract onExpand handler to top level to avoid Rules of Hooks violation
  // This was previously defined inside renderRow which violates Rules of Hooks
  const handleExpandMessage = useCallback((index, msg) => {
    // If this is the last message, scroll to bottom to show details
    if (index === displayMessages.length - 1) {
      // Small delay to allow expansion animation to start/finish
      setTimeout(() => {
        virtuosoRef.current?.scrollTo({ top: 999999, behavior: 'smooth' });
      }, 100);
    }
  }, [displayMessages.length, virtuosoRef]);

  // FIX: Extract toggle handler to top level to avoid Rules of Hooks violation
  // This was previously defined inside JSX onClick which violates Rules of Hooks
  const toggleMemberList = useCallback(() => {
    setShowMemberList(prev => !prev);
  }, []);

  // Determine last outgoing message index and seen status
  // Must be before early return because renderRow depends on these
  let lastOutgoingIdx = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.senderId === currentUser?.uid) {
      lastOutgoingIdx = i;
      break;
    }
  }
  let lastOutgoingSeen = false;
  if (lastOutgoingIdx >= 0) {
    try {
      const conv = activeConv;
      const msg = messages[lastOutgoingIdx];
      if (conv && msg?.createdAt) {
        const members = Array.isArray(conv.memberIds) ? conv.memberIds : [];
        const otherId = currentUser?.uid ? members.find(id => id !== currentUser.uid) : null;
        const lastSeenRaw = otherId && conv.lastSeenAt ? conv.lastSeenAt[otherId] : null;
        const lastSeen = lastSeenRaw && lastSeenRaw.toDate ? lastSeenRaw.toDate() : lastSeenRaw || null;
        if (lastSeen) {
          lastOutgoingSeen = msg.createdAt <= lastSeen;
        }
      }
    } catch (_) { }
  }

  // Render row function for Virtuoso
  // IMPORTANT: Virtuoso's itemContent callback signature is (index, dataItem)
  // index = the array index (number)
  // msg = the data item from the array (message object)
  // NOTE: Keys are managed by computeItemKey prop, not by Fragment key
  // FIX D2: Wrap renderRow in useCallback to prevent Virtuoso list re-renders
  const renderRow = useCallback((index, msg) => {
    const getTime = (m) => {
      if (!m?.createdAt) return 0;
      return m.createdAt.toMillis ? m.createdAt.toMillis() : (m.createdAt.getTime ? m.createdAt.getTime() : new Date(m.createdAt).getTime());
    };

    const currentTimestamp = getTime(msg);
    const prevMsg = index > 0 ? messages[index - 1] : null;
    const prevTimestamp = prevMsg ? getTime(prevMsg) : 0;

    // Show session header if first message OR gap > 30 minutes
    const showSessionHeader = index === 0 || (currentTimestamp - prevTimestamp > 30 * 60 * 1000);

    // FIX Issue 5: Virtuoso requires a single wrapper DOM node for `ResizeObserver` to correctly measure item heights and prevent scroll jumps.
    // Also, use padding instead of margins to prevent CSS margin collapsing between items.
    return (
      <div className="chat-window__message-row-wrapper" style={{ paddingBottom: '8px' }}>
        {showSessionHeader && (
          <div className="chat-message-session-header" style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#9ca3af',
            paddingTop: '16px',
            paddingBottom: '8px',
            fontWeight: 500
          }}>
            {(() => {
              const sessionDate = new Date(currentTimestamp);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);

              if (sessionDate.toDateString() === today.toDateString()) {
                return 'Today';
              } else if (sessionDate.toDateString() === yesterday.toDateString()) {
                return 'Yesterday';
              } else {
                return sessionDate.toLocaleDateString();
              }
            })()}
          </div>
        )}
        <MessageBubble
          message={msg}
          isGroup={isGroup}
          onExpand={() => handleExpandMessage(index, msg)}
        />
        {!searchQuery && index === lastOutgoingIdx && lastOutgoingSeen && (
          <div className="chat-window__seen" aria-live="polite" style={{ fontSize: 12, color: '#6b7280', paddingTop: 6, textAlign: 'right' }}>
            Seen
          </div>
        )}
      </div>
    );
  }, [messages, isGroup, lastOutgoingIdx, lastOutgoingSeen, handleExpandMessage, searchQuery]);

  // Early return if no active conversation
  if (!activeConversationId) {
    return (
      <div className="chat-window chat-window--empty">
        <h2>Select a conversation</h2>
        <p>Choose a chat from the left to get started.</p>
      </div>
    );
  }

  return (
    <div className="chat-window">
      {/* Group Chat Header */}
      {!hideHeader && isGroup && activeConv && (
        <div className="chat-window__header">
          <div className="chat-window__header-info">
            <h2 className="chat-window__title">{activeConv.title || 'Group Chat'}</h2>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="chat-window__members-btn"
                onClick={toggleMemberList}
              >
                {memberCount} members
                <span style={{ fontSize: '10px' }}>{showMemberList ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}</span>
              </button>

              {showMemberList && (
                <div className="chat-window__member-list" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  width: '240px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 50,
                  padding: '8px 0'
                }}>
                  <div style={{ padding: '4px 12px 8px', borderBottom: '1px solid #f3f4f6', fontSize: '12px', fontWeight: 600, color: '#9ca3af' }}>
                    Group Members
                  </div>
                  {memberDetails.map(member => (
                    <div key={member.id} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 600, color: '#6b7280' }}>
                        {(member.displayName || member.username || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member.displayName || member.username || 'Unknown User'}
                        {member.id === currentUser?.uid && <span style={{ color: '#9ca3af', marginLeft: '4px' }}>(You)</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="chat-window__header-actions">
            <button
              type="button"
              className="chat-window__search-btn"
              onClick={() => setShowSearch(!showSearch)}
              title="Search messages"
            >
              <IconSearch size={18} />
            </button>
            <button type="button" className="chat-window__leave-btn" onClick={handleLeaveGroup}>
              Leave group
            </button>
          </div>
        </div>
      )}

      {/* Direct Chat Header */}
      {!hideHeader && !isGroup && activeConv && (
        <div className="chat-window__header">
          <div style={{ position: 'relative', marginRight: 12, flexShrink: 0 }}>
            {directUser?.photoURL ? (
              <img
                src={directUser.photoURL}
                alt={directUser.displayName}
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, color: '#6b7280' }}>
                {(directUser?.displayName?.[0] || '?').toUpperCase()}
              </div>
            )}
            {isDirectUserOnline && (
              <span className="floating-chat-header-online-dot" style={{ border: '2px solid var(--color-chat-accent, #0084ff)' }} />
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 className="chat-window__title">
              {directUser?.displayName || 'User'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(255, 255, 255, 0.85)' }}>
              {/* FIX L-11: Show "Last seen" time for offline users */}
              <span>{isDirectUserOnline
                ? 'Online'
                : (() => {
                  const otherPresence = presenceByUserId?.[otherUserId];
                  const lastActive = otherPresence?.lastActiveAt;
                  if (!lastActive) return 'Offline';
                  const ts = lastActive?.toDate ? lastActive.toDate() : (lastActive instanceof Date ? lastActive : new Date(lastActive));
                  const diffMin = Math.round((Date.now() - ts.getTime()) / 60000);
                  if (diffMin < 1) return 'Last seen just now';
                  if (diffMin < 60) return `Last seen ${diffMin}m ago`;
                  const diffH = Math.floor(diffMin / 60);
                  if (diffH < 24) return `Last seen ${diffH}h ago`;
                  return `Last seen ${Math.floor(diffH / 24)}d ago`;
                })()
              }</span>
            </div>
          </div>
          <button
            type="button"
            className="chat-window__search-btn"
            onClick={() => setShowSearch(!showSearch)}
            title="Search messages"
          >
            <IconSearch size={18} />
          </button>
        </div>
      )}

      {showSearch && (
        <div className="chat-window__search">
          <input
            type="text"
            className="chat-window__search-input"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchQuery('');
                setShowSearch(false);
              }
            }}
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              className="chat-window__search-clear"
              onClick={() => setSearchQuery('')}
            >
              <IconX size={16} />
            </button>
          )}
        </div>
      )}

      {/* Virtuoso Message List */}
      <div
        className="chat-window__messages"
        aria-live="polite"
        role="log"
        style={{ height: '100%' }}
      >
        {/* Empty state: show only when genuinely empty (not loading) */}
        {!displayMessages.length && !searchQuery && activeConversationId && (
          <div className="chat-skeleton-container">
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>
              💬 Start the conversation! Send a message below.
            </div>
          </div>
        )}

        <Virtuoso
          ref={virtuosoRef}
          data={displayMessages}
          itemContent={renderRow}
          computeItemKey={(index, msg) => getStableMessageKey(msg)}
          initialTopMostItemIndex={Math.max(0, displayMessages.length - 1)}
          followOutput={(isAtBottom) => isAtBottom ? 'smooth' : false}
          alignToBottom
          atBottomThreshold={100}
          atBottomStateChange={(atBottom) => {
            setShowScrollButton(!atBottom);
          }}
          atTopStateChange={(atTop) => {
            setIsAtTop(atTop);
          }}
          style={{ height: '100%' }}
          className="chat-window__virtuoso"
          components={virtuosoComponents}
        />

        {searchQuery && displayMessages.length === 0 && !isSearching && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
            No results found for "{searchQuery}"
          </div>
        )}
        {isSearching && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
            Searching...
          </div>
        )}
      </div>

      <div className="chat-window__scroll-anchor">
        {showScrollButton && (
          <button
            className="chat-window__scroll-to-bottom"
            onClick={scrollToBottom}
            type="button"
            aria-label="Scroll to bottom"
          >
            <IconChevronDown size={24} />
          </button>
        )}
      </div>

      <MessageInputBar conversationId={activeConversationId} />
    </div>
  );
};

export default ChatWindow;
